import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Bot,
  Check,
  X,
  RefreshCw,
  DollarSign,
  Clock,
  Activity,
  AlertCircle,
  Settings2,
  Play,
  Save,
  Plus,
  Edit3,
  Trash2,
  Key,
  Power,
  Eye,
  EyeOff,
  Globe,
  Shield,
  Zap,
  Star,
  Database,
  Upload,
  FileText,
  ExternalLink
} from 'lucide-react';
import Modal from '../ui/Modal';
import { safeApolloHref } from '../../utils/safeApolloHref';

const ScraperSettingsTab = () => {
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Main state
  const [activeSection, setActiveSection] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Overview data
  const [scraperOverview, setScraperOverview] = useState(null);

  // Apify state (existing functionality)
  const [apifySettings, setApifySettings] = useState(null);
  const [availableActors, setAvailableActors] = useState([]);
  const [usageStats, setUsageStats] = useState([]);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loadingStats, setLoadingStats] = useState(false);

  // Token Management State (existing)
  const [apifyTokens, setApifyTokens] = useState([]);
  const [loadingTokens, setLoadingTokens] = useState(true);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [editingToken, setEditingToken] = useState(null);
  const [tokenForm, setTokenForm] = useState({
    email: '',
    token: '',
    name: ''
  });
  const [showTokenValues, setShowTokenValues] = useState({}); // For showing/hiding token values

  // Actor Management State (existing)
  const [apifyActors, setApifyActors] = useState([]);
  const [loadingActors, setLoadingActors] = useState(true);
  const [showActorModal, setShowActorModal] = useState(false);
  const [editingActor, setEditingActor] = useState(null);
  const [actorForm, setActorForm] = useState({
    id: '',
    name: '',
    enabled: true,
    daily_limit: 100,
    cost_per_1000_contacts: 0.0,
    limit_message: 'Daily limit reached for this actor. Please try again tomorrow.',
    default_params: '{}',
    input_mapping: '{}'
  });
  const [todayStats, setTodayStats] = useState([]);

  // Apify Settings Form data
  const [apifySettingsForm, setApifySettingsForm] = useState({
    primary_actor: 'code_crafter/apollo-io-scraper',
    fallback_actor: 'microworlds/apollo-scraper',
    primary_actor_enabled: true,
    fallback_actor_enabled: true
  });

  // AmpleLeads state (new)
  const [ampleLeadsSettings, setAmpleLeadsSettings] = useState(null);
  const [ampleLeadsApiKeys, setAmpleLeadsApiKeys] = useState([]);
  const [rateLimitStatus, setRateLimitStatus] = useState({});
  const [showAmpleLeadsModal, setShowAmpleLeadsModal] = useState(false);
  const [editingAmpleLeadsKey, setEditingAmpleLeadsKey] = useState(null);
  const [ampleLeadsKeyForm, setAmpleLeadsKeyForm] = useState({
    name: '',
    key: '',
    plan_type: '',
    custom_daily_limit: '',
    is_default: false,
    notes: ''
  });

  // Bright Data state
  const [brightDataSettings, setBrightDataSettings] = useState(null);
  const [brightDataApiKeys, setBrightDataApiKeys] = useState([]);
  const [showBrightDataModal, setShowBrightDataModal] = useState(false);
  const [editingBrightDataKey, setEditingBrightDataKey] = useState(null);
  const [brightDataKeyForm, setBrightDataKeyForm] = useState({
    name: '',
    key: '',
    custom_daily_limit: '',
    is_default: false,
    notes: ''
  });

  // Service Routing state (Phase A)
  const [serviceRouting, setServiceRouting] = useState(null);
  const [savingRouting, setSavingRouting] = useState(false);

  // Lead Finder Service state
  const [leadFinderService, setLeadFinderService] = useState(null);
  const [savingLeadFinder, setSavingLeadFinder] = useState(false);

  // Manual Jobs state
  const [manualJobs, setManualJobs] = useState([]);
  const [fulfillmentConfig, setFulfillmentConfig] = useState(null);
  const [loadingManualJobs, setLoadingManualJobs] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(null);
  const [selectedJobForUpload, setSelectedJobForUpload] = useState(null);
  const [userMessage, setUserMessage] = useState('All systems are operational. New scraping requests are expected to be completed within 24–48 hours.');

  // Plan type options for AmpleLeads
  const planOptions = [
    { value: 'free', label: 'Free (20/day)', defaultLimit: 20 },
    { value: 'starter', label: 'Starter (50/day)', defaultLimit: 50 },
    { value: 'growth', label: 'Growth (70/day)', defaultLimit: 70 },
    { value: 'scale', label: 'Scale (100/day)', defaultLimit: 100 },
    { value: 'pro', label: 'Pro (150/day)', defaultLimit: 150 },
    { value: 'enterprise', label: 'Enterprise (200/day)', defaultLimit: 200 },
    { value: 'ultimate', label: 'Ultimate (250/day)', defaultLimit: 250 }
  ];

  // Load data on component mount
  useEffect(() => {
    loadScraperOverview();
    loadServiceRouting();
    loadLeadFinderService();
    // Pre-load both AmpleLeads and Apify data so it's available when user clicks tabs
    loadAmpleLeadsData();
    loadApifyData();
  }, []);

  // Load scraper overview
  const loadScraperOverview = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.get('/api/admin/scraper-admin/overview', {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setScraperOverview(response.data);
    } catch (error) {
      console.error('Error loading scraper overview:', error);
      setError('Failed to load scraper overview');
    } finally {
      setLoading(false);
    }
  };

  // Load service routing configuration
  const loadServiceRouting = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.get('/api/admin/scraper-admin/service-routing', {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setServiceRouting(response.data);
    } catch (error) {
      console.error('Error loading service routing:', error);
      // Set default if loading fails
      setServiceRouting({
        apollo_scraper_service: 'apify',
        enable_fallback: true
      });
    }
  };

  // Update service routing
  const handleUpdateServiceRouting = async (service) => {
    try {
      setSavingRouting(true);
      setError('');
      const adminToken = localStorage.getItem('admin_token');

      await axios.put('/api/admin/scraper-admin/service-routing',
        { apollo_scraper_service: service },
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );

      // Reload routing config and overview
      await loadServiceRouting();
      await loadScraperOverview();

      setSuccessMessage(`Apollo Scraper now uses ${service === 'apify' ? 'Apify' : 'AmpleLeads'}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating service routing:', error);
      setError('Failed to update service routing');
    } finally {
      setSavingRouting(false);
    }
  };

  // Load Lead Finder service configuration
  const loadLeadFinderService = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.get('/api/admin/scraper-admin/lead-finder-service', {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setLeadFinderService(response.data);
    } catch (error) {
      console.error('Error loading Lead Finder service:', error);
      // Set default if loading fails
      setLeadFinderService({
        lead_finder_actor_id: 'peakydev/leads-scraper-ppe',
        enabled: true
      });
    }
  };

  // Update Lead Finder service configuration
  const handleUpdateLeadFinderService = async (actorId) => {
    try {
      setSavingLeadFinder(true);
      setError('');
      const adminToken = localStorage.getItem('admin_token');

      await axios.put('/api/admin/scraper-admin/lead-finder-service',
        { lead_finder_actor_id: actorId },
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );

      // Reload config
      await loadLeadFinderService();

      setSuccessMessage(`Lead Finder now uses ${actorId}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating Lead Finder service:', error);
      setError('Failed to update Lead Finder service');
    } finally {
      setSavingLeadFinder(false);
    }
  };

  // Load AmpleLeads data
  const loadAmpleLeadsData = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      const authHeaders = { headers: { 'Authorization': `Bearer ${adminToken}` } };

      // Load settings and API keys
      const [settingsResponse, keysResponse, rateLimitsResponse] = await Promise.all([
        axios.get('/api/admin/scraper-admin/ampleleads/settings', authHeaders),
        axios.get('/api/admin/scraper-admin/ampleleads/api-keys', authHeaders),
        axios.get('/api/admin/scraper-admin/ampleleads/rate-limits', authHeaders).catch(() => ({ data: {} }))
      ]);

      setAmpleLeadsSettings(settingsResponse.data);
      setAmpleLeadsApiKeys(keysResponse.data);
      setRateLimitStatus(rateLimitsResponse.data);
    } catch (error) {
      console.error('Error loading AmpleLeads data:', error);
      setError('Failed to load AmpleLeads settings');
    } finally {
      setLoading(false);
    }
  };

  // Load Apify data (existing functionality)
  const loadApifyData = async () => {
    try {
      setLoading(true);
      const adminToken = localStorage.getItem('admin_token');
      const authHeaders = { headers: { 'Authorization': `Bearer ${adminToken}` } };

      const [settingsResponse, actorsResponse, tokensResponse] = await Promise.all([
        axios.get('/api/admin/scraper-admin/apify-settings', authHeaders),
        axios.get('/api/admin/scraper-admin/apify-actors', authHeaders).catch(() => ({ data: [] })),
        axios.get('/api/admin/scraper-admin/apify-tokens', authHeaders).catch(() => ({ data: [] }))
      ]);

      setApifySettings(settingsResponse.data);
      setAvailableActors(actorsResponse.data?.actors || actorsResponse.data || []);
      setApifyTokens(tokensResponse.data?.tokens || tokensResponse.data || []);
    } catch (error) {
      console.error('Error loading Apify data:', error);
      setError('Failed to load Apify settings');
    } finally {
      setLoading(false);
      setLoadingTokens(false);
      setLoadingActors(false);
    }
  };

  // Load Manual Jobs data
  const loadManualJobs = async () => {
    try {
      setLoadingManualJobs(true);
      const adminToken = localStorage.getItem('admin_token');
      const authHeaders = { headers: { 'Authorization': `Bearer ${adminToken}` } };

      const [jobsRes, configRes] = await Promise.all([
        axios.get('/api/admin/scraper-admin/ampleleads/manual-jobs/pending', authHeaders),
        axios.get('/api/admin/scraper-admin/ampleleads/fulfillment-config', authHeaders)
      ]);

      setManualJobs(jobsRes.data.jobs || []);
      setFulfillmentConfig(configRes.data);
      setUserMessage(configRes.data.manual_mode_user_message || 'All systems are operational. New scraping requests are expected to be completed within 24–48 hours.');
    } catch (error) {
      console.error('Error loading manual jobs:', error);
      setError('Failed to load manual jobs');
    } finally {
      setLoadingManualJobs(false);
    }
  };

  // Handle section change
  const handleSectionChange = (section) => {
    setActiveSection(section);
    setError('');
    setSuccessMessage('');

    // Load data based on section
    if (section === 'ampleleads') {
      loadAmpleLeadsData();
    } else if (section === 'brightdata') {
      loadBrightDataData();
    } else if (section === 'apify') {
      loadApifyData();
    } else if (section === 'overview') {
      loadScraperOverview();
    } else if (section === 'manual-jobs') {
      loadManualJobs();
    }
  };

  // Manual Jobs functions
  const handleCsvUpload = async (jobId, file) => {
    try {
      setUploadingCsv(jobId);
      setError('');
      const adminToken = localStorage.getItem('admin_token');

      const formData = new FormData();
      formData.append('csv_file', file);

      await axios.post(
        `/api/admin/scraper-admin/ampleleads/manual-jobs/${jobId}/upload-csv`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${adminToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setSuccessMessage('CSV uploaded successfully! Job completed.');
      loadManualJobs(); // Refresh list
    } catch (error) {
      // Build a diagnosable message: backend detail > HTTP status > network error.
      // A bare "Failed to upload CSV" previously hid whether the request was
      // rejected by the server, blocked by the proxy, or never sent at all.
      let message;
      if (error.response?.data?.detail) {
        message = error.response.data.detail;
      } else if (error.response) {
        message = `Failed to upload CSV (HTTP ${error.response.status} ${error.response.statusText || ''})`.trim();
        if (error.response.status === 413) {
          message += ' — file too large for the server limit';
        }
      } else {
        message = `Failed to upload CSV (network error: ${error.message || 'request never reached the server'} — check connection and retry)`;
      }
      setError(message);
    } finally {
      setUploadingCsv(null);
      setSelectedJobForUpload(null);
    }
  };

  const handleFulfillmentModeToggle = async (newMode) => {
    try {
      setSavingSettings(true);
      setError('');
      const adminToken = localStorage.getItem('admin_token');

      await axios.put(
        '/api/admin/scraper-admin/ampleleads/fulfillment-mode',
        { mode: newMode },
        {
          headers: { 'Authorization': `Bearer ${adminToken}` },
          params: {
            mode: newMode,
            user_message: userMessage
          }
        }
      );

      setSuccessMessage(`Fulfillment mode changed to ${newMode}`);
      loadManualJobs(); // Refresh config
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update fulfillment mode');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleUserMessageSave = async () => {
    try {
      setSavingSettings(true);
      setError('');
      const adminToken = localStorage.getItem('admin_token');

      await axios.put(
        '/api/admin/scraper-admin/ampleleads/fulfillment-mode',
        { mode: fulfillmentConfig.fulfillment_mode },
        {
          headers: { 'Authorization': `Bearer ${adminToken}` },
          params: {
            mode: fulfillmentConfig.fulfillment_mode,
            user_message: userMessage
          }
        }
      );

      setSuccessMessage('User message updated successfully');
      loadManualJobs(); // Refresh config
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to update user message');
    } finally {
      setSavingSettings(false);
    }
  };

  // AmpleLeads API key management functions
  const handleAmpleLeadsKeySubmit = async (e) => {
    e.preventDefault();
    try {
      setSavingSettings(true);

      const submitData = {
        ...ampleLeadsKeyForm,
        custom_daily_limit: ampleLeadsKeyForm.custom_daily_limit ? parseInt(ampleLeadsKeyForm.custom_daily_limit) : null
      };

      if (editingAmpleLeadsKey) {
        await axios.put(`/api/admin/scraper-admin/ampleleads/api-keys/${editingAmpleLeadsKey.id}`, submitData);
        setSuccessMessage('API key updated successfully');
      } else {
        await axios.post('/api/admin/scraper-admin/ampleleads/api-keys', submitData);
        setSuccessMessage('API key added successfully');
      }

      setShowAmpleLeadsModal(false);
      setEditingAmpleLeadsKey(null);
      setAmpleLeadsKeyForm({
        name: '',
        key: '',
        plan_type: '',
        custom_daily_limit: '',
        is_default: false,
        notes: ''
      });

      loadAmpleLeadsData();
    } catch (error) {
      console.error('Error saving AmpleLeads key:', error);
      setError(error.response?.data?.detail || 'Failed to save API key');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleSetDefaultAmpleLeadsKey = async (keyId) => {
    try {
      await axios.put(`/api/admin/scraper-admin/ampleleads/api-keys/${keyId}/set-default`);
      setSuccessMessage('Default API key updated');
      loadAmpleLeadsData();
    } catch (error) {
      console.error('Error setting default key:', error);
      setError('Failed to set default API key');
    }
  };

  const handleDeleteAmpleLeadsKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;

    try {
      await axios.delete(`/api/admin/scraper-admin/ampleleads/api-keys/${keyId}`);
      setSuccessMessage('API key deleted successfully');
      loadAmpleLeadsData();
    } catch (error) {
      console.error('Error deleting key:', error);
      setError('Failed to delete API key');
    }
  };

  const openAmpleLeadsModal = (key = null) => {
    if (key) {
      setEditingAmpleLeadsKey(key);
      setAmpleLeadsKeyForm({
        name: key.name,
        key: '', // Don't populate the actual key for security
        plan_type: key.plan_type || '',
        custom_daily_limit: key.custom_daily_limit || '',
        is_default: key.is_default,
        notes: key.notes || ''
      });
    } else {
      setEditingAmpleLeadsKey(null);
      setAmpleLeadsKeyForm({
        name: '',
        key: '',
        plan_type: '',
        custom_daily_limit: '',
        is_default: false,
        notes: ''
      });
    }
    setShowAmpleLeadsModal(true);
  };

  // Bright Data Functions
  const loadBrightDataData = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');

      // Load API keys
      const keysResponse = await axios.get(`${API_BASE_URL}/api/admin/scraper-admin/brightdata/api-keys`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setBrightDataApiKeys(keysResponse.data || []);

      // Load settings
      const settingsResponse = await axios.get(`${API_BASE_URL}/api/admin/scraper-admin/brightdata/settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setBrightDataSettings(settingsResponse.data);
    } catch (error) {
      console.error('Error loading Bright Data data:', error);
      setError('Failed to load Bright Data data');
    }
  };

  const handleSetDefaultBrightDataKey = async (keyId) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      await axios.put(`${API_BASE_URL}/api/admin/scraper-admin/brightdata/api-keys/${keyId}/set-default`, {}, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setSuccessMessage('Default API key updated');
      loadBrightDataData();
    } catch (error) {
      console.error('Error setting default key:', error);
      setError('Failed to set default API key');
    }
  };

  const handleDeleteBrightDataKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;

    try {
      const adminToken = localStorage.getItem('admin_token');
      await axios.delete(`${API_BASE_URL}/api/admin/scraper-admin/brightdata/api-keys/${keyId}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });
      setSuccessMessage('API key deleted successfully');
      loadBrightDataData();
    } catch (error) {
      console.error('Error deleting key:', error);
      setError('Failed to delete API key');
    }
  };

  const openBrightDataModal = (key = null) => {
    if (key) {
      setEditingBrightDataKey(key);
      setBrightDataKeyForm({
        name: key.name,
        key: '', // Don't populate the actual key for security
        custom_daily_limit: key.custom_daily_limit || '',
        is_default: key.is_default,
        notes: key.notes || ''
      });
    } else {
      setEditingBrightDataKey(null);
      setBrightDataKeyForm({
        name: '',
        key: '',
        custom_daily_limit: '',
        is_default: false,
        notes: ''
      });
    }
    setShowBrightDataModal(true);
  };

  const handleBrightDataKeySubmit = async (e) => {
    e.preventDefault();

    try {
      const adminToken = localStorage.getItem('admin_token');

      if (editingBrightDataKey) {
        // Update existing key
        const updateData = {
          name: brightDataKeyForm.name,
          custom_daily_limit: brightDataKeyForm.custom_daily_limit || null,
          is_default: brightDataKeyForm.is_default,
          notes: brightDataKeyForm.notes,
          active: true
        };

        // Only include key if it's provided
        if (brightDataKeyForm.key) {
          updateData.key = brightDataKeyForm.key;
        }

        await axios.put(
          `${API_BASE_URL}/api/admin/scraper-admin/brightdata/api-keys/${editingBrightDataKey.id}`,
          updateData,
          { headers: { 'Authorization': `Bearer ${adminToken}` } }
        );
        setSuccessMessage('API key updated successfully');
      } else {
        // Create new key
        await axios.post(
          `${API_BASE_URL}/api/admin/scraper-admin/brightdata/api-keys`,
          brightDataKeyForm,
          { headers: { 'Authorization': `Bearer ${adminToken}` } }
        );
        setSuccessMessage('API key added successfully');
      }

      setShowBrightDataModal(false);
      setEditingBrightDataKey(null);
      setBrightDataKeyForm({
        name: '',
        key: '',
        custom_daily_limit: '',
        is_default: false,
        notes: ''
      });

      loadBrightDataData();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving Bright Data key:', error);
      setError(error.response?.data?.detail || 'Failed to save API key');
    }
  };

  const handleUpdateBrightDataSettings = async (updates) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      await axios.put(
        `${API_BASE_URL}/api/admin/scraper-admin/brightdata/settings`,
        updates,
        { headers: { 'Authorization': `Bearer ${adminToken}` } }
      );

      // Update local state
      setBrightDataSettings(prev => ({ ...prev, ...updates }));
      setSuccessMessage('Settings updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error updating Bright Data settings:', error);
      setError('Failed to update settings');
    }
  };

  // Token Management API Functions
  const fetchApifyTokens = async () => {
    try {
      setLoadingTokens(true);
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/scraper-admin/apify-tokens`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setApifyTokens(response.data.tokens || []);
      setError('');
    } catch (error) {
      console.error('Error fetching APIFY tokens:', error);
      setApifyTokens([]); // Set to empty array on error
      setError('Failed to fetch APIFY tokens');
    } finally {
      setLoadingTokens(false);
    }
  };

  const saveApifyToken = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');

      if (editingToken) {
        // Update existing token
        await axios.put(`${API_BASE_URL}/api/admin/scraper-admin/apify-tokens/${editingToken.id}`, tokenForm, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        setSuccessMessage('APIFY token updated successfully!');
      } else {
        // Create new token
        await axios.post(`${API_BASE_URL}/api/admin/scraper-admin/apify-tokens`, tokenForm, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        setSuccessMessage('APIFY token created successfully!');
      }

      // Reset form and close modal
      setTokenForm({ email: '', token: '', name: '' });
      setEditingToken(null);
      setShowTokenModal(false);

      // Refresh tokens list
      await fetchApifyTokens();

      setError('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving APIFY token:', error);
      setError(error.response?.data?.detail || 'Failed to save APIFY token');
    }
  };

  const deleteApifyToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to delete this APIFY token? This action cannot be undone.')) {
      return;
    }

    try {
      const adminToken = localStorage.getItem('admin_token');
      await axios.delete(`${API_BASE_URL}/api/admin/scraper-admin/apify-tokens/${tokenId}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setSuccessMessage('APIFY token deleted successfully!');
      await fetchApifyTokens();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting APIFY token:', error);
      setError(error.response?.data?.detail || 'Failed to delete APIFY token');
    }
  };

  const activateApifyToken = async (tokenId) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      await axios.post(`${API_BASE_URL}/api/admin/scraper-admin/apify-tokens/${tokenId}/activate`, {}, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setSuccessMessage('APIFY token activated successfully!');
      await fetchApifyTokens();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error activating APIFY token:', error);
      setError(error.response?.data?.detail || 'Failed to activate APIFY token');
    }
  };

  const handleTokenFormChange = (field, value) => {
    setTokenForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const openTokenModal = (token = null) => {
    if (token) {
      setEditingToken(token);
      setTokenForm({
        email: token.email,
        token: '', // Don't populate the token field for security
        name: token.name || ''
      });
    } else {
      setEditingToken(null);
      setTokenForm({ email: '', token: '', name: '' });
    }
    setShowTokenModal(true);
  };

  const toggleTokenVisibility = (tokenId) => {
    setShowTokenValues(prev => ({
      ...prev,
      [tokenId]: !prev[tokenId]
    }));
  };

  // Actor Management API Functions
  const fetchApifyActors = async () => {
    try {
      setLoadingActors(true);
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/scraper-admin/apify-actors`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setApifyActors(response.data.actors || []);
      setError('');
    } catch (error) {
      console.error('Error fetching APIFY actors:', error);
      setApifyActors([]); // Set to empty array on error
      setError('Failed to fetch APIFY actors');
    } finally {
      setLoadingActors(false);
    }
  };

  const fetchTodayStats = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/scraper-admin/apify-actors/stats/today`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setTodayStats(response.data || []);
    } catch (error) {
      console.error('Error fetching today stats:', error);
      setTodayStats([]); // Set to empty array on error
    }
  };

  const saveApifyActor = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');

      // Parse JSON fields
      let parsedDefaultParams = {};
      let parsedInputMapping = {};

      try {
        parsedDefaultParams = actorForm.default_params ? JSON.parse(actorForm.default_params) : {};
      } catch (e) {
        setError('Invalid JSON in Default Parameters field');
        return;
      }

      try {
        parsedInputMapping = actorForm.input_mapping ? JSON.parse(actorForm.input_mapping) : {};
      } catch (e) {
        setError('Invalid JSON in Input Mapping field');
        return;
      }

      const payload = {
        ...actorForm,
        default_params: parsedDefaultParams,
        input_mapping: parsedInputMapping,
        cost_per_1000_contacts: parseFloat(actorForm.cost_per_1000_contacts) || 0.0,
        daily_limit: parseInt(actorForm.daily_limit) || 100
      };

      if (editingActor) {
        // Update existing actor
        await axios.put(`${API_BASE_URL}/api/admin/scraper-admin/apify-actors/${encodeURIComponent(editingActor.id)}`, payload, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        setSuccessMessage('APIFY actor updated successfully!');
      } else {
        // Create new actor
        await axios.post(`${API_BASE_URL}/api/admin/scraper-admin/apify-actors`, payload, {
          headers: { 'Authorization': `Bearer ${adminToken}` }
        });
        setSuccessMessage('APIFY actor created successfully!');
      }

      // Reset form and close modal
      resetActorForm();
      setShowActorModal(false);

      // Refresh actors and stats
      await fetchApifyActors();
      await fetchTodayStats();

      setError('');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving APIFY actor:', error);
      setError(error.response?.data?.detail || 'Failed to save APIFY actor');
    }
  };

  const deleteApifyActor = async (actorId) => {
    if (!window.confirm('Are you sure you want to delete this APIFY actor? This action cannot be undone.')) {
      return;
    }

    try {
      const adminToken = localStorage.getItem('admin_token');
      await axios.delete(`${API_BASE_URL}/api/admin/scraper-admin/apify-actors/${encodeURIComponent(actorId)}`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setSuccessMessage('APIFY actor deleted successfully!');
      await fetchApifyActors();
      await fetchTodayStats();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting APIFY actor:', error);
      setError(error.response?.data?.detail || 'Failed to delete APIFY actor');
    }
  };

  const toggleApifyActor = async (actorId) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      await axios.post(`${API_BASE_URL}/api/admin/scraper-admin/apify-actors/${encodeURIComponent(actorId)}/toggle`, {}, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setSuccessMessage('APIFY actor status updated successfully!');
      await fetchApifyActors();
      await fetchTodayStats();

      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error toggling APIFY actor:', error);
      setError(error.response?.data?.detail || 'Failed to toggle APIFY actor');
    }
  };

  const resetActorForm = () => {
    setActorForm({
      id: '',
      name: '',
      enabled: true,
      daily_limit: 100,
      cost_per_1000_contacts: 0.0,
      limit_message: 'Daily limit reached for this actor. Please try again tomorrow.',
      default_params: '{}',
      input_mapping: '{}'
    });
    setEditingActor(null);
  };

  const openActorModal = (actor = null) => {
    if (actor) {
      setEditingActor(actor);
      setActorForm({
        id: actor.id,
        name: actor.name,
        enabled: actor.enabled,
        daily_limit: actor.daily_limit,
        cost_per_1000_contacts: actor.cost_per_1000_contacts,
        limit_message: actor.limit_message,
        default_params: JSON.stringify(actor.default_params, null, 2),
        input_mapping: JSON.stringify(actor.input_mapping, null, 2)
      });
    } else {
      resetActorForm();
    }
    setShowActorModal(true);
  };

  const handleActorFormChange = (field, value) => {
    setActorForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const loadDefaultActors = async () => {
    if (!window.confirm('This will load/update 4 default actors (code_crafter, microworlds, supreme_coder, apidojo). Continue?')) {
      return;
    }

    try {
      setLoadingActors(true);
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/scraper-admin/apify-actors/load-defaults`, {}, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setSuccessMessage(response.data.message);

      // Refresh data
      await fetchApifyActors();
      await fetchTodayStats();

      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (error) {
      console.error('Error loading default actors:', error);
      setError(error.response?.data?.detail || 'Failed to load default actors');
    } finally {
      setLoadingActors(false);
    }
  };

  // Apify Settings API Functions
  const fetchApifySettings = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/scraper-admin/apify-settings`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setApifySettings(response.data);
      setApifySettingsForm({
        primary_actor: response.data.primary_actor || 'code_crafter/apollo-io-scraper',
        fallback_actor: response.data.fallback_actor || 'microworlds/apollo-scraper',
        primary_actor_enabled: response.data.primary_actor_enabled !== false,
        fallback_actor_enabled: response.data.fallback_actor_enabled !== false
      });

      setError('');
    } catch (error) {
      console.error('Error fetching Apify settings:', error);
      setError('Failed to fetch Apify settings');
    }
  };

  const fetchAvailableActors = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/scraper-admin/apify-actors/schemas`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setAvailableActors(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching available actors:', error);
      setError('Failed to fetch available actors');
    }
  };

  const fetchUsageStats = async () => {
    try {
      setLoadingStats(true);
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_BASE_URL}/api/admin/scraper-admin/apify-usage?days=30`, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setUsageStats(response.data);
      setError('');
    } catch (error) {
      console.error('Error fetching usage stats:', error);
      setError('Failed to fetch usage statistics');
    } finally {
      setLoadingStats(false);
      setLoading(false);
    }
  };

  const saveApifySettings = async () => {
    try {
      setSavingSettings(true);
      const adminToken = localStorage.getItem('admin_token');

      const response = await axios.put(`${API_BASE_URL}/api/admin/scraper-admin/apify-settings`, apifySettingsForm, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setApifySettings(response.data.settings);
      setSuccessMessage('Apify settings saved successfully!');
      setError('');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving Apify settings:', error);
      setError(error.response?.data?.detail || 'Failed to save Apify settings');
    } finally {
      setSavingSettings(false);
    }
  };

  const testActorConnection = async (actorId) => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const response = await axios.post(`${API_BASE_URL}/api/admin/test-actor/${encodeURIComponent(actorId)}`, {}, {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      setSuccessMessage(`Successfully connected to ${actorId}`);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error(`Error testing actor ${actorId}:`, error);
      setError(`Failed to connect to ${actorId}: ${error.response?.data?.detail || 'Unknown error'}`);
    }
  };

  const handleApifyInputChange = (field, value) => {
    setApifySettingsForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const formatRuntime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const remainingSeconds = Math.floor(seconds % 60);

    if (hours > 0) {
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      return `${remainingSeconds}s`;
    }
  };

  // Utility functions
  const clearMessages = () => {
    setError('');
    setSuccessMessage('');
  };

  const getRateLimitColor = (canScrape, canStatus) => {
    if (canScrape && canStatus) return 'text-green-600';
    if (canScrape || canStatus) return 'text-yellow-600';
    return 'text-red-600';
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  // Render functions
  const renderSectionTabs = () => (
    <div className="flex border-b border-gray-200 mb-6">
      {[
        { id: 'overview', label: 'Overview', icon: Activity },
        { id: 'ampleleads', label: 'Enhanced Apollo', icon: Globe },
        { id: 'manual-jobs', label: 'Manual Jobs', icon: Upload },
        { id: 'brightdata', label: 'Bright Data', icon: Database },
        { id: 'apify', label: 'Apify', icon: Bot }
      ].map(({ id, label, icon: Icon }) => (
        <button
          key={id}
          onClick={() => handleSectionChange(id)}
          className={`flex items-center gap-2 px-4 py-2 font-medium border-b-2 transition-colors ${
            activeSection === id
              ? 'border-blue-500 text-blue-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Icon size={20} />
          {label}
        </button>
      ))}
    </div>
  );

  const renderOverview = () => {
    if (!scraperOverview) return null;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Apify Overview */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Bot className="text-purple-600" size={24} />
              <h3 className="text-lg font-semibold text-gray-900">Apify Integration</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className="text-green-600 font-medium">Active</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Primary Actor:</span>
                <span className="text-gray-900">{scraperOverview.apify.primary_actor}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Actors:</span>
                <span className="text-gray-900">{scraperOverview.apify.total_actors}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Available:</span>
                <span className="text-gray-900">{scraperOverview.apify.available_actors}</span>
              </div>
            </div>
          </div>

          {/* AmpleLeads Overview */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <Globe className="text-blue-600" size={24} />
              <h3 className="text-lg font-semibold text-gray-900">Enhanced Apollo Integration</h3>
            </div>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`font-medium ${scraperOverview.ampleleads.enabled ? 'text-green-600' : 'text-gray-400'}`}>
                  {scraperOverview.ampleleads.enabled ? 'Active' : 'Inactive'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Total API Keys:</span>
                <span className="text-gray-900">{scraperOverview.ampleleads.total_api_keys}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Active Keys:</span>
                <span className="text-gray-900">{scraperOverview.ampleleads.active_api_keys}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Default Key Set:</span>
                <span className={`font-medium ${scraperOverview.ampleleads.default_key_configured ? 'text-green-600' : 'text-red-600'}`}>
                  {scraperOverview.ampleleads.default_key_configured ? 'Yes' : 'No'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Service Routing Configuration (Phase A) */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Settings2 className="text-gray-600" size={24} />
              <h3 className="text-lg font-semibold text-gray-900">Apollo Scraper Service</h3>
            </div>
            {savingRouting && (
              <span className="text-sm text-gray-500 flex items-center gap-2">
                <RefreshCw size={14} className="animate-spin" />
                Saving...
              </span>
            )}
          </div>

          {!serviceRouting ? (
            <div className="text-center py-8">
              <RefreshCw className="animate-spin mx-auto mb-2 text-gray-400" size={24} />
              <p className="text-sm text-gray-500">Loading configuration...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Service Selector */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Which service should handle Apollo scraping jobs?
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Apify Option */}
                  <button
                    onClick={() => handleUpdateServiceRouting('apify')}
                    disabled={savingRouting}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      serviceRouting.apollo_scraper_service === 'apify'
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Bot className={serviceRouting.apollo_scraper_service === 'apify' ? 'text-purple-600' : 'text-gray-400'} size={20} />
                        <div className="text-left">
                          <div className="font-semibold text-gray-900">Apify</div>
                          <div className="text-xs text-gray-500">Fast & reliable</div>
                        </div>
                      </div>
                      {serviceRouting.apollo_scraper_service === 'apify' && (
                        <Check className="text-purple-600" size={20} />
                      )}
                    </div>
                  </button>

                  {/* AmpleLeads Option */}
                  <button
                    onClick={() => handleUpdateServiceRouting('ampleleads')}
                    disabled={savingRouting || !scraperOverview.routing.can_use_ampleleads}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      serviceRouting.apollo_scraper_service === 'ampleleads'
                        ? 'border-blue-600 bg-blue-50'
                        : scraperOverview.routing.can_use_ampleleads
                        ? 'border-gray-200 hover:border-gray-300'
                        : 'border-gray-200 opacity-50 cursor-not-allowed'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Globe className={serviceRouting.apollo_scraper_service === 'ampleleads' ? 'text-blue-600' : 'text-gray-400'} size={20} />
                        <div className="text-left">
                          <div className="font-semibold text-gray-900">AmpleLeads</div>
                          <div className="text-xs text-gray-500">
                            {scraperOverview.routing.can_use_ampleleads ? '1-24hr jobs' : 'Configure API keys first'}
                          </div>
                        </div>
                      </div>
                      {serviceRouting.apollo_scraper_service === 'ampleleads' && (
                        <Check className="text-blue-600" size={20} />
                      )}
                    </div>
                  </button>
                </div>
              </div>

              {/* Status Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-blue-600 flex-shrink-0" size={20} />
                  <div className="text-sm text-gray-700">
                    <p className="font-medium mb-1">Current Configuration:</p>
                    <p>
                      Apollo scraping jobs will use <strong>{serviceRouting.apollo_scraper_service === 'ampleleads' ? 'AmpleLeads' : 'Apify'}</strong>
                      {serviceRouting.enable_fallback && ' with Apify fallback if unavailable'}.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Lead Finder Service Configuration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Settings2 className="text-emerald-600" size={24} />
              <h3 className="text-lg font-semibold text-gray-900">Lead Finder Service</h3>
            </div>
            {savingLeadFinder && (
              <span className="text-sm text-gray-500 flex items-center gap-2">
                <RefreshCw size={14} className="animate-spin" />
                Saving...
              </span>
            )}
          </div>

          {!leadFinderService ? (
            <div className="text-center py-8">
              <RefreshCw className="animate-spin mx-auto mb-2 text-gray-400" size={24} />
              <p className="text-sm text-gray-500">Loading configuration...</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Actor Type Selection (Radio Buttons) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Active Lead Finder Actor
                </label>
                <div className="space-y-3">
                  {/* Actor A (Peaky) */}
                  <label className="flex items-start gap-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="active_actor"
                      value="peaky"
                      checked={(leadFinderService.active_actor || 'peaky') === 'peaky'}
                      onChange={async (e) => {
                        setSavingLeadFinder(true);
                        try {
                          const adminToken = localStorage.getItem('admin_token');
                          await axios.put('/api/admin/scraper-admin/lead-finder-service',
                            { active_actor: 'peaky' },
                            { headers: { 'Authorization': `Bearer ${adminToken}` } }
                          );
                          setLeadFinderService({ ...leadFinderService, active_actor: 'peaky' });
                          setSuccessMessage('Switched to Actor A');
                        } catch (error) {
                          console.error('Error updating actor:', error);
                          setError('Failed to update active actor');
                        } finally {
                          setSavingLeadFinder(false);
                        }
                      }}
                      disabled={savingLeadFinder}
                      className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Actor A</div>
                      <div className="text-sm text-gray-600 mt-1">
                        Standard lead finder with count-based preview
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Actor ID: {leadFinderService.peaky_actor_id || 'peakydev/leads-scraper-ppe'}
                      </div>
                    </div>
                  </label>

                  {/* Actor B (Pipeline Labs) */}
                  <label className="flex items-start gap-3 p-4 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="active_actor"
                      value="pipelinelabs"
                      checked={leadFinderService.active_actor === 'pipelinelabs'}
                      onChange={async (e) => {
                        setSavingLeadFinder(true);
                        try {
                          const adminToken = localStorage.getItem('admin_token');
                          await axios.put('/api/admin/scraper-admin/lead-finder-service',
                            { active_actor: 'pipelinelabs' },
                            { headers: { 'Authorization': `Bearer ${adminToken}` } }
                          );
                          setLeadFinderService({ ...leadFinderService, active_actor: 'pipelinelabs' });
                          setSuccessMessage('Switched to Actor B');
                        } catch (error) {
                          console.error('Error updating actor:', error);
                          setError('Failed to update active actor');
                        } finally {
                          setSavingLeadFinder(false);
                        }
                      }}
                      disabled={savingLeadFinder}
                      className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500"
                    />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">Actor B</div>
                      <div className="text-sm text-gray-600 mt-1">
                        Advanced lead finder with preview modal and masked contact data
                      </div>
                      <div className="text-xs text-gray-500 mt-1">
                        Actor ID: {leadFinderService.pipelinelabs_actor_id || 'pipelinelabs/lead-scraper-apollo-zoominfo-lusha-ppe'}
                      </div>
                    </div>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Users will see a single "Lead Finder" menu item. The form and preview experience will change based on the selected actor.
                </p>
              </div>

              {/* Status Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="text-emerald-600 flex-shrink-0" size={20} />
                  <div className="text-sm text-gray-700">
                    <p className="font-medium mb-1">Current Configuration:</p>
                    <p>
                      Active Actor: <strong>{(leadFinderService.active_actor || 'peaky') === 'peaky' ? 'Actor A' : 'Actor B'}</strong>
                    </p>
                    <p className="text-xs text-gray-500 mt-2">
                      The Lead Finder interface will automatically adapt to the selected actor without any user intervention.
                    </p>
                  </div>
                </div>
              </div>

              {/* Migration Button */}
              <div className="flex justify-end">
                <button
                  onClick={async () => {
                    if (!window.confirm('Run Lead Finder configuration migration?\\n\\nThis will initialize/update the actor configuration in the database.')) {
                      return;
                    }

                    setSavingLeadFinder(true);
                    try {
                      const adminToken = localStorage.getItem('admin_token');
                      const response = await axios.post(
                        '/api/admin/scraper-admin/lead-finder-service/migrate',
                        {},
                        { headers: { 'Authorization': `Bearer ${adminToken}` } }
                      );

                      setSuccessMessage(`Migration completed: ${response.data.results.map(r => r.action).join(', ')}`);
                      await loadLeadFinderService(); // Reload config
                    } catch (error) {
                      console.error('Migration error:', error);
                      setError(error.response?.data?.detail || 'Failed to run migration');
                    } finally {
                      setSavingLeadFinder(false);
                    }
                  }}
                  disabled={savingLeadFinder}
                  className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {savingLeadFinder ? (
                    <>
                      <RefreshCw size={14} className="animate-spin" />
                      Running Migration...
                    </>
                  ) : (
                    <>
                      <Database size={14} />
                      Run Database Migration
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderAmpleLeadsSection = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Enhanced Apollo API Keys</h3>
          <p className="text-sm text-gray-600">Manage Enhanced Apollo API keys with custom daily limits</p>
        </div>
        <button
          onClick={() => openAmpleLeadsModal()}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus size={16} />
          Add API Key
        </button>
      </div>

      {/* API Keys Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Key</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Limit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate Limits</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {ampleLeadsApiKeys.map((key) => {
                const rateLimits = rateLimitStatus[key.id] || {};
                return (
                  <tr key={key.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900">{key.name}</span>
                        {key.is_default && (
                          <Star size={16} className="ml-2 text-yellow-500" />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                      {key.masked_key}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {key.plan_type ? key.plan_type.toUpperCase() : 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {key.custom_daily_limit || 'Plan Default'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        key.active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {key.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className={`${getRateLimitColor(rateLimits.can_scrape, rateLimits.can_status)}`}>
                        <div>Scrape: {rateLimits.can_scrape ? '✓' : '✗'}</div>
                        <div>Status: {rateLimits.can_status ? '✓' : '✗'}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        {!key.is_default && (
                          <button
                            onClick={() => handleSetDefaultAmpleLeadsKey(key.id)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Set as default"
                          >
                            <Star size={16} />
                          </button>
                        )}
                        <button
                          onClick={() => openAmpleLeadsModal(key)}
                          className="text-gray-600 hover:text-gray-800"
                          title="Edit"
                        >
                          <Edit3 size={16} />
                        </button>
                        <button
                          onClick={() => handleDeleteAmpleLeadsKey(key.id)}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {ampleLeadsApiKeys.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Globe size={48} className="mx-auto mb-4 text-gray-300" />
            <p>No Enhanced Apollo API keys configured</p>
            <p className="text-sm">Add your first API key to enable Enhanced Apollo integration</p>
          </div>
        )}
      </div>
    </div>
  );

  const renderBrightDataSection = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Bright Data API Keys</h3>
          <p className="text-sm text-gray-600">Manage Bright Data API keys for web scraping and AI enrichment</p>
        </div>
        <button
          onClick={() => openBrightDataModal()}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          <Plus size={16} />
          Add API Key
        </button>
      </div>

      {/* API Keys Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">API Key</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Daily Limit</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Used</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {brightDataApiKeys.map((key) => (
                <tr key={key.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900">{key.name}</span>
                      {key.is_default && (
                        <Star size={16} className="ml-2 text-yellow-500" fill="currentColor" />
                      )}
                    </div>
                    {key.notes && (
                      <div className="text-xs text-gray-500 mt-1">{key.notes}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-mono">
                    {key.masked_key}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {key.custom_daily_limit ? `${key.custom_daily_limit}/day` : 'Unlimited'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      key.active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {key.active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {key.last_used_at ? new Date(key.last_used_at).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      {!key.is_default && (
                        <button
                          onClick={() => handleSetDefaultBrightDataKey(key.id)}
                          className="text-purple-600 hover:text-purple-800"
                          title="Set as default"
                        >
                          <Star size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => openBrightDataModal(key)}
                        className="text-gray-600 hover:text-gray-800"
                        title="Edit"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => handleDeleteBrightDataKey(key.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {brightDataApiKeys.length === 0 && (
          <div className="p-8 text-center text-gray-500">
            <Database size={48} className="mx-auto mb-4 text-gray-300" />
            <p>No Bright Data API keys configured</p>
            <p className="text-sm">Add your first API key to enable Bright Data web scraping</p>
          </div>
        )}
      </div>

      {/* Settings Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h4 className="text-md font-semibold text-gray-900 mb-4">Bright Data Settings</h4>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Auto-rotate Keys
            </label>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={brightDataSettings?.auto_rotate_keys || false}
                onChange={(e) => handleUpdateBrightDataSettings({ auto_rotate_keys: e.target.checked })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Request Timeout (seconds)
            </label>
            <input
              type="number"
              value={brightDataSettings?.request_timeout || 30}
              onChange={(e) => handleUpdateBrightDataSettings({ request_timeout: parseInt(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              min="10"
              max="120"
            />
          </div>
        </div>
      </div>
    </div>
  );

  // Render Manual Jobs Section
  const renderManualJobsSection = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-semibold mb-4">Manual Fulfillment Mode</h3>

        {/* Fulfillment Mode Toggle */}
        {fulfillmentConfig && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Current Mode: <span className={`${fulfillmentConfig.fulfillment_mode === 'manual' ? 'text-orange-600' : 'text-green-600'}`}>
                  {fulfillmentConfig.fulfillment_mode === 'manual' ? '🚨 Manual' : '✅ API'}
                </span></p>
                <p className="text-sm text-gray-600 mt-1">
                  {fulfillmentConfig.fulfillment_mode === 'manual'
                    ? 'New jobs will require manual CSV upload'
                    : 'New jobs will use AmpleLeads API automatically'}
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Notification email: {fulfillmentConfig.manual_notification_email}
                </p>
              </div>
              <button
                onClick={() => handleFulfillmentModeToggle(fulfillmentConfig.fulfillment_mode === 'manual' ? 'api' : 'manual')}
                disabled={savingSettings}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  fulfillmentConfig.fulfillment_mode === 'manual'
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-orange-600 hover:bg-orange-700 text-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {savingSettings ? (
                  <RefreshCw className="animate-spin" size={16} />
                ) : (
                  fulfillmentConfig.fulfillment_mode === 'manual' ? 'Switch to API Mode' : 'Switch to Manual Mode'
                )}
              </button>
            </div>
          </div>
        )}

        {/* User Message for Manual Mode */}
        {fulfillmentConfig && fulfillmentConfig.fulfillment_mode === 'manual' && (
          <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                User Message (Displayed on Dashboard)
              </label>
              <p className="text-xs text-gray-600 mb-3">
                This message will be shown to users on their dashboard when manual fulfillment mode is active.
              </p>
              <textarea
                value={userMessage}
                onChange={(e) => setUserMessage(e.target.value)}
                maxLength={500}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter a message to display to users..."
              />
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-gray-500">
                  {userMessage.length}/500 characters
                </span>
                <button
                  onClick={handleUserMessageSave}
                  disabled={savingSettings}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {savingSettings ? (
                    <>
                      <RefreshCw className="animate-spin" size={16} />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} />
                      Save Message
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pending Jobs List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-md font-semibold">Pending Manual Jobs ({manualJobs.length})</h4>
            <button
              onClick={loadManualJobs}
              disabled={loadingManualJobs}
              className="text-blue-600 hover:text-blue-700 flex items-center gap-2"
            >
              <RefreshCw className={loadingManualJobs ? 'animate-spin' : ''} size={16} />
              Refresh
            </button>
          </div>

          {loadingManualJobs ? (
            <div className="text-center py-8">
              <RefreshCw className="animate-spin mx-auto mb-2 text-gray-400" size={24} />
              <p className="text-sm text-gray-500">Loading manual jobs...</p>
            </div>
          ) : manualJobs.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <FileText className="mx-auto mb-2 text-gray-400" size={48} />
              <p className="text-gray-600">No pending manual jobs</p>
              <p className="text-sm text-gray-500 mt-1">
                Jobs waiting for CSV upload will appear here
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {manualJobs.map((job) => (
                <div key={job.job_id} className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h5 className="font-semibold">{job.list_name}</h5>
                        <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded">
                          Waiting for CSV
                        </span>
                      </div>

                      <div className="grid grid-cols-2 gap-2 text-sm text-gray-600 mb-3">
                        <div>
                          <span className="font-medium">Job ID:</span>
                          <code className="ml-2 bg-gray-100 px-2 py-1 rounded text-xs">{job.job_id}</code>
                        </div>
                        <div>
                          <span className="font-medium">Contacts:</span> {job.contacts_requested?.toLocaleString()}
                        </div>
                        <div>
                          <span className="font-medium">User:</span> {job.user_name} ({job.user_email})
                        </div>
                        <div>
                          <span className="font-medium">Elapsed:</span> {job.elapsed_hours} hours
                        </div>
                      </div>

                      <div className="flex items-start gap-2 p-2 bg-yellow-50 rounded border border-yellow-200">
                        <ExternalLink size={16} className="text-yellow-700 mt-0.5 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-yellow-800 mb-1">Apollo URL:</p>
                          {safeApolloHref(job.apollo_url) ? (
                            <a
                              href={safeApolloHref(job.apollo_url)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:text-blue-700 break-all"
                            >
                              {job.apollo_url}
                            </a>
                          ) : (
                            <span className="text-xs text-red-700 break-all">
                              {job.apollo_url} (not a valid Apollo.io link - do not open)
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="ml-4">
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".csv,text/csv,text/comma-separated-values,application/csv,application/vnd.ms-excel"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              handleCsvUpload(job.job_id, file);
                            }
                          }}
                          className="hidden"
                          disabled={uploadingCsv === job.job_id}
                        />
                        <div className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                          uploadingCsv === job.job_id
                            ? 'bg-gray-300 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}>
                          {uploadingCsv === job.job_id ? (
                            <>
                              <RefreshCw className="animate-spin" size={16} />
                              Uploading...
                            </>
                          ) : (
                            <>
                              <Upload size={16} />
                              Upload CSV
                            </>
                          )}
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h5 className="font-semibold text-blue-900 mb-2">📋 Instructions</h5>
          <ol className="list-decimal list-inside space-y-1 text-sm text-blue-800">
            <li>Copy the Apollo URL from the job above</li>
            <li>Log in to <a href="https://ampleleads.io" target="_blank" rel="noopener noreferrer" className="underline">AmpleLeads</a></li>
            <li>Paste the URL and start the scraping job</li>
            <li>Download the CSV when complete</li>
            <li>Click "Upload CSV" button above to upload and complete the job</li>
          </ol>
          <p className="text-xs text-blue-700 mt-2">
            ℹ️ The system will automatically count rows, process refunds if needed, and complete the job.
          </p>
        </div>
      </div>
    </div>
  );

  const renderApifySection = () => {
    if (!apifySettings && !apifyTokens.length && !apifyActors.length) {
      // Initial load
      fetchApifyTokens();
      fetchApifyActors();
      fetchTodayStats();
      fetchApifySettings();
      fetchAvailableActors();
      fetchUsageStats();
    }

    return (
      <div className="space-y-8">
        {/* APIFY Token Management */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Key className="w-6 h-6 text-yellow-600" />
                <h2 className="text-xl font-semibold text-gray-900">APIFY Token Management</h2>
              </div>
              <button
                onClick={() => openTokenModal()}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Token
              </button>
            </div>
          </div>

          <div className="p-6">
            {loadingTokens ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-yellow-600 mx-auto mb-2" />
                <p className="text-gray-600">Loading APIFY tokens...</p>
              </div>
            ) : (apifyTokens && apifyTokens.length > 0) ? (
              <div className="space-y-4">
                {apifyTokens.map((token) => (
                  <div key={token.id} className={`p-4 border rounded-lg ${token.is_active ? 'border-green-300 bg-green-50' : 'border-gray-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium text-gray-900">
                            {token.name || 'Unnamed Token'}
                          </h3>
                          {token.is_active && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <Power className="w-3 h-3" />
                              Active
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">
                          <strong>Email:</strong> {token.email}
                        </p>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm text-gray-600">
                            <strong>Token:</strong> {showTokenValues[token.id] ? token.token_preview : token.token_preview}
                          </span>
                          <button
                            onClick={() => toggleTokenVisibility(token.id)}
                            className="p-1 text-gray-400 hover:text-gray-600"
                          >
                            {showTokenValues[token.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        </div>
                        <div className="text-xs text-gray-500 space-x-4">
                          <span>Created: {new Date(token.created_at).toLocaleDateString()}</span>
                          <span>Updated: {new Date(token.updated_at).toLocaleDateString()}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 ml-4">
                        {!token.is_active && (
                          <button
                            onClick={() => activateApifyToken(token.id)}
                            className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors flex items-center gap-2"
                          >
                            <Power className="w-3 h-3" />
                            Activate
                          </button>
                        )}
                        <button
                          onClick={() => openTokenModal(token)}
                          className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors flex items-center gap-2"
                        >
                          <Edit3 className="w-3 h-3" />
                          Edit
                        </button>
                        <button
                          onClick={() => deleteApifyToken(token.id)}
                          className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors flex items-center gap-2"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Key className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No APIFY tokens configured</p>
                <p className="text-sm text-gray-400 mt-1">
                  Add an APIFY token to enable scraping functionality
                </p>
                <button
                  onClick={() => openTokenModal()}
                  className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <Plus className="w-4 h-4" />
                  Add Your First Token
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Actor Management */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Bot className="w-6 h-6 text-blue-600" />
                <h2 className="text-xl font-semibold text-gray-900">Actor Management</h2>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={loadDefaultActors}
                  disabled={loadingActors}
                  className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2 text-sm disabled:opacity-50"
                  title="Temporary setup button - loads the 4 default actors"
                >
                  {loadingActors ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <Settings2 className="w-4 h-4" />
                      Load Default Actors
                    </>
                  )}
                </button>
                <button
                  onClick={() => openActorModal()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  Add Actor
                </button>
              </div>
            </div>
          </div>

          <div className="p-6">
            {loadingActors ? (
              <div className="text-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-gray-600">Loading APIFY actors...</p>
              </div>
            ) : (apifyActors && apifyActors.length > 0) ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {apifyActors.map((actor) => {
                  const todayStat = (todayStats || []).find(stat => stat.actor_id === actor.id);
                  const todayRuns = todayStat?.today_runs || 0;
                  const dailyLimit = actor.daily_limit;
                  const todayCost = todayStat?.today_cost || 0;
                  const percentageUsed = todayStat?.percentage_used || 0;
                  const isLimitReached = todayStat?.is_limit_reached || false;

                  return (
                    <div key={actor.id} className={`p-4 border rounded-lg ${!actor.enabled ? 'border-gray-300 bg-gray-50' : isLimitReached ? 'border-red-300 bg-red-50' : 'border-green-300 bg-green-50'}`}>
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-medium text-gray-900 truncate" title={actor.name}>
                            {actor.name}
                          </h3>
                          <p className="text-sm text-gray-600 truncate" title={actor.id}>
                            {actor.id}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 ml-2">
                          {actor.enabled ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                              <Check className="w-3 h-3" />
                              Active
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">
                              <X className="w-3 h-3" />
                              Disabled
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="space-y-2 mb-3">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Today's Runs:</span>
                          <span className={`font-medium ${isLimitReached ? 'text-red-600' : 'text-gray-900'}`}>
                            {todayRuns}/{dailyLimit}
                          </span>
                        </div>

                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full transition-all duration-300 ${
                              isLimitReached ? 'bg-red-500' :
                              percentageUsed > 80 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(percentageUsed, 100)}%` }}
                          ></div>
                        </div>

                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Cost/1k:</span>
                          <span className="font-medium text-gray-900">${actor.cost_per_1000_contacts.toFixed(2)}</span>
                        </div>

                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Today's Cost:</span>
                          <span className="font-medium text-gray-900">${todayCost.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => toggleApifyActor(actor.id)}
                          className={`px-2 py-1 text-xs rounded-md transition-colors flex items-center gap-1 ${
                            actor.enabled
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-green-100 text-green-700 hover:bg-green-200'
                          }`}
                        >
                          <Power className="w-3 h-3" />
                          {actor.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          onClick={() => openActorModal(actor)}
                          className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors flex items-center gap-1"
                        >
                          <Edit3 className="w-3 h-3" />
                          Edit
                        </button>
                        <button
                          onClick={() => deleteApifyActor(actor.id)}
                          className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Bot className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No APIFY actors configured</p>
                <p className="text-sm text-gray-400 mt-1">
                  Add actors to enable dynamic scraping capabilities
                </p>
                <button
                  onClick={() => openActorModal()}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 mx-auto"
                >
                  <Plus className="w-4 h-4" />
                  Add Your First Actor
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
          {/* Actor Configuration */}
          <div className="space-y-6">
            {/* Settings Configuration Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <Settings2 className="w-6 h-6 text-blue-600" />
                  <h2 className="text-xl font-semibold text-gray-900">Actor Configuration</h2>
                </div>
              </div>

              <div className="p-6 space-y-6">
                {/* Primary Actor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Primary Actor
                  </label>
                  <div className="space-y-3">
                    <select
                      value={apifySettingsForm.primary_actor}
                      onChange={(e) => handleApifyInputChange('primary_actor', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {apifyActors && apifyActors.filter(actor => actor.enabled).map(actor => (
                        <option key={actor.id} value={actor.id}>
                          {actor.name} ({actor.id})
                        </option>
                      ))}
                      {/* Fallback options if no actors loaded */}
                      {(!apifyActors || apifyActors.length === 0) && (
                        <>
                          <option value="code_crafter/apollo-io-scraper">code_crafter/apollo-io-scraper</option>
                          <option value="microworlds/apollo-scraper">microworlds/apollo-scraper</option>
                        </>
                      )}
                    </select>

                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={apifySettingsForm.primary_actor_enabled}
                          onChange={(e) => handleApifyInputChange('primary_actor_enabled', e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Enabled</span>
                      </label>

                      <button
                        onClick={() => testActorConnection(apifySettingsForm.primary_actor)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors flex items-center gap-2"
                      >
                        <Play className="w-3 h-3" />
                        Test Connection
                      </button>
                    </div>
                  </div>
                </div>

                {/* Fallback Actor */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fallback Actor
                  </label>
                  <div className="space-y-3">
                    <select
                      value={apifySettingsForm.fallback_actor}
                      onChange={(e) => handleApifyInputChange('fallback_actor', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {apifyActors && apifyActors.filter(actor => actor.enabled).map(actor => (
                        <option key={actor.id} value={actor.id}>
                          {actor.name} ({actor.id})
                        </option>
                      ))}
                      {/* Fallback options if no actors loaded */}
                      {(!apifyActors || apifyActors.length === 0) && (
                        <>
                          <option value="code_crafter/apollo-io-scraper">code_crafter/apollo-io-scraper</option>
                          <option value="microworlds/apollo-scraper">microworlds/apollo-scraper</option>
                        </>
                      )}
                    </select>

                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={apifySettingsForm.fallback_actor_enabled}
                          onChange={(e) => handleApifyInputChange('fallback_actor_enabled', e.target.checked)}
                          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                        />
                        <span className="text-sm text-gray-700">Enabled</span>
                      </label>

                      <button
                        onClick={() => testActorConnection(apifySettingsForm.fallback_actor)}
                        className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors flex items-center gap-2"
                      >
                        <Play className="w-3 h-3" />
                        Test Connection
                      </button>
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={saveApifySettings}
                    disabled={savingSettings}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {savingSettings ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save Settings
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Available Actors */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center gap-3">
                  <Bot className="w-6 h-6 text-green-600" />
                  <h2 className="text-xl font-semibold text-gray-900">Available Actors</h2>
                </div>
              </div>

              <div className="p-6">
                {(availableActors && availableActors.length > 0) ? (
                  <div className="space-y-4">
                    {availableActors.map((actor) => {
                      // Find today's stats for this actor
                      const todayStat = (todayStats || []).find(stat => stat.actor_id === actor.id);
                      const todayRuns = todayStat?.today_runs || 0;
                      const dailyLimit = todayStat?.daily_limit || actor.daily_limit || 100;
                      const todayCost = todayStat?.today_cost || 0;
                      const costPer1000 = actor.cost_per_1000_contacts || 0;
                      const isLimitReached = todayStat?.is_limit_reached || false;

                      return (
                        <div key={actor.id} className={`p-4 border rounded-lg ${
                          !actor.is_available ? 'border-red-200 bg-red-50' :
                          isLimitReached ? 'border-orange-200 bg-orange-50' :
                          'border-gray-200 bg-white'
                        }`}>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium text-gray-900">{actor.id}</h3>
                            <div className="flex items-center gap-2">
                              {actor.is_available ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                                  <Check className="w-3 h-3" />
                                  Available
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                                  <X className="w-3 h-3" />
                                  Unavailable
                                </span>
                              )}
                              {isLimitReached && (
                                <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs font-medium">
                                  <AlertCircle className="w-3 h-3" />
                                  Limit Reached
                                </span>
                              )}
                            </div>
                          </div>

                          <p className="text-sm text-gray-600 mb-3">{actor.title}</p>
                          {actor.description && (
                            <p className="text-xs text-gray-500 mb-3">{actor.description}</p>
                          )}

                          {/* Today's Activity Stats */}
                          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                            <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              Today's Activity
                            </h4>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                              <div className="text-center">
                                <div className="text-gray-600">Runs</div>
                                <div className={`font-semibold ${isLimitReached ? 'text-red-600' : 'text-gray-900'}`}>
                                  {todayRuns}/{dailyLimit}
                                </div>
                              </div>

                              <div className="text-center">
                                <div className="text-gray-600">Cost/1k</div>
                                <div className="font-semibold text-gray-900">
                                  ${costPer1000.toFixed(2)}
                                </div>
                              </div>

                              <div className="text-center">
                                <div className="text-gray-600">Today Cost</div>
                                <div className="font-semibold text-green-600">
                                  ${todayCost.toFixed(2)}
                                </div>
                              </div>

                              <div className="text-center">
                                <div className="text-gray-600">Usage</div>
                                <div className={`font-semibold ${
                                  isLimitReached ? 'text-red-600' :
                                  todayRuns/dailyLimit > 0.8 ? 'text-orange-600' : 'text-green-600'
                                }`}>
                                  {((todayRuns/dailyLimit) * 100).toFixed(0)}%
                                </div>
                              </div>
                            </div>

                            {/* Progress Bar */}
                            <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                              <div
                                className={`h-2 rounded-full transition-all duration-300 ${
                                  isLimitReached ? 'bg-red-500' :
                                  todayRuns/dailyLimit > 0.8 ? 'bg-orange-500' : 'bg-green-500'
                                }`}
                                style={{ width: `${Math.min((todayRuns/dailyLimit) * 100, 100)}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">No actors available</p>
                )}
              </div>
            </div>
          </div>

          {/* Usage Statistics */}
          <div>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Activity className="w-6 h-6 text-purple-600" />
                    <h2 className="text-xl font-semibold text-gray-900">Usage Statistics (Last 30 Days)</h2>
                  </div>
                  <button
                    onClick={fetchUsageStats}
                    disabled={loadingStats}
                    className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors flex items-center gap-2"
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingStats ? 'animate-spin' : ''}`} />
                    Refresh
                  </button>
                </div>
              </div>

              <div className="p-6">
                {loadingStats ? (
                  <div className="text-center py-8">
                    <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
                    <p className="text-gray-600">Loading usage statistics...</p>
                  </div>
                ) : (usageStats && usageStats.length > 0) ? (
                  <div className="space-y-6">
                    {usageStats.map((stat) => (
                      <div key={stat.actor_id} className="border border-gray-200 rounded-lg p-5">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="font-medium text-gray-900">{stat.actor_name}</h3>
                          <span className="text-sm text-gray-500">{stat.actor_id}</span>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div className="bg-blue-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Play className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-medium text-blue-900">Total Runs</span>
                            </div>
                            <p className="text-lg font-semibold text-blue-700">{stat.total_runs}</p>
                          </div>

                          <div className="bg-green-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <Check className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-medium text-green-900">Successful</span>
                            </div>
                            <p className="text-lg font-semibold text-green-700">{stat.successful_runs}</p>
                          </div>

                          <div className="bg-red-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <X className="w-4 h-4 text-red-600" />
                              <span className="text-sm font-medium text-red-900">Failed</span>
                            </div>
                            <p className="text-lg font-semibold text-red-700">{stat.failed_runs}</p>
                          </div>

                          <div className="bg-purple-50 p-3 rounded-lg">
                            <div className="flex items-center gap-2 mb-1">
                              <DollarSign className="w-4 h-4 text-purple-600" />
                              <span className="text-sm font-medium text-purple-900">Total Cost</span>
                            </div>
                            <p className="text-lg font-semibold text-purple-700">
                              {formatCurrency(stat.total_cost_usd)}
                            </p>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" />
                            <span>Avg Runtime: {formatRuntime(stat.avg_runtime_seconds)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Activity className="w-4 h-4" />
                            <span>Compute Units: {stat.total_compute_units.toFixed(2)}</span>
                          </div>
                        </div>

                        {stat.last_run_date && (
                          <div className="mt-3 pt-3 border-t border-gray-200">
                            <p className="text-xs text-gray-500">
                              Last run: {new Date(stat.last_run_date).toLocaleString()}
                            </p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Activity className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">No usage statistics available</p>
                    <p className="text-sm text-gray-400 mt-1">
                      Statistics will appear here once you start running scraping jobs
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="scraper-settings-tab">
      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} className="text-red-600" />
          <span className="text-red-700">{error}</span>
          <button onClick={clearMessages} className="ml-auto text-red-600 hover:text-red-800">
            <X size={16} />
          </button>
        </div>
      )}

      {successMessage && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
          <Check size={20} className="text-green-600" />
          <span className="text-green-700">{successMessage}</span>
          <button onClick={clearMessages} className="ml-auto text-green-600 hover:text-green-800">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Section Tabs */}
      {renderSectionTabs()}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-8">
          <RefreshCw className="animate-spin text-blue-600" size={24} />
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      )}

      {/* Section Content */}
      {!loading && (
        <>
          {activeSection === 'overview' && renderOverview()}
          {activeSection === 'ampleleads' && renderAmpleLeadsSection()}
          {activeSection === 'manual-jobs' && renderManualJobsSection()}
          {activeSection === 'brightdata' && renderBrightDataSection()}
          {activeSection === 'apify' && renderApifySection()}
        </>
      )}

      {/* AmpleLeads API Key Modal */}
      <Modal
        isOpen={showAmpleLeadsModal}
        onClose={() => setShowAmpleLeadsModal(false)}
        title={editingAmpleLeadsKey ? 'Edit Enhanced Apollo API Key' : 'Add Enhanced Apollo API Key'}
      >
        <form onSubmit={handleAmpleLeadsKeySubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Key Name *
            </label>
            <input
              type="text"
              value={ampleLeadsKeyForm.name}
              onChange={(e) => setAmpleLeadsKeyForm({ ...ampleLeadsKeyForm, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="e.g., Production Key 1"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key * {editingAmpleLeadsKey && <span className="text-gray-500">(leave blank to keep existing)</span>}
            </label>
            <input
              type="password"
              value={ampleLeadsKeyForm.key}
              onChange={(e) => setAmpleLeadsKeyForm({ ...ampleLeadsKeyForm, key: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Enter Enhanced Apollo API key"
              required={!editingAmpleLeadsKey}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Plan Type
            </label>
            <select
              value={ampleLeadsKeyForm.plan_type}
              onChange={(e) => setAmpleLeadsKeyForm({ ...ampleLeadsKeyForm, plan_type: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Select plan type</option>
              {planOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Custom Daily Limit
            </label>
            <input
              type="number"
              min="1"
              max="1000"
              value={ampleLeadsKeyForm.custom_daily_limit}
              onChange={(e) => setAmpleLeadsKeyForm({ ...ampleLeadsKeyForm, custom_daily_limit: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Leave blank to use plan default"
            />
            <p className="mt-1 text-xs text-gray-500">
              Override the plan's default daily limit (1-1000 requests per 24 hours)
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={ampleLeadsKeyForm.is_default}
                onChange={(e) => setAmpleLeadsKeyForm({ ...ampleLeadsKeyForm, is_default: e.target.checked })}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Set as default API key</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={ampleLeadsKeyForm.notes}
              onChange={(e) => setAmpleLeadsKeyForm({ ...ampleLeadsKeyForm, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              placeholder="Add any notes about this API key"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              disabled={savingSettings}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {savingSettings ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
              {editingAmpleLeadsKey ? 'Update' : 'Add'} API Key
            </button>
            <button
              type="button"
              onClick={() => setShowAmpleLeadsModal(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Bright Data API Key Modal */}
      <Modal
        isOpen={showBrightDataModal}
        onClose={() => setShowBrightDataModal(false)}
        title={editingBrightDataKey ? 'Edit Bright Data API Key' : 'Add Bright Data API Key'}
      >
        <form onSubmit={handleBrightDataKeySubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Key Name *
            </label>
            <input
              type="text"
              value={brightDataKeyForm.name}
              onChange={(e) => setBrightDataKeyForm({ ...brightDataKeyForm, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="e.g., Production Key 1"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              API Key (Bearer Token) * {editingBrightDataKey && <span className="text-gray-500">(leave blank to keep existing)</span>}
            </label>
            <input
              type="password"
              value={brightDataKeyForm.key}
              onChange={(e) => setBrightDataKeyForm({ ...brightDataKeyForm, key: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter Bright Data API token"
              required={!editingBrightDataKey}
            />
            <p className="mt-1 text-xs text-gray-500">
              Get your API token from Bright Data account settings
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Custom Daily Limit
            </label>
            <input
              type="number"
              min="1"
              value={brightDataKeyForm.custom_daily_limit}
              onChange={(e) => setBrightDataKeyForm({ ...brightDataKeyForm, custom_daily_limit: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Leave blank for unlimited"
            />
            <p className="mt-1 text-xs text-gray-500">
              Set a daily request limit for this key (optional)
            </p>
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={brightDataKeyForm.is_default}
                onChange={(e) => setBrightDataKeyForm({ ...brightDataKeyForm, is_default: e.target.checked })}
                className="rounded border-gray-300 text-purple-600 focus:ring-purple-500"
              />
              <span className="text-sm text-gray-700">Set as default API key</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes (optional)
            </label>
            <textarea
              value={brightDataKeyForm.notes}
              onChange={(e) => setBrightDataKeyForm({ ...brightDataKeyForm, notes: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              rows={3}
              placeholder="Add any notes about this API key"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 flex items-center justify-center gap-2"
            >
              <Save size={16} />
              {editingBrightDataKey ? 'Update' : 'Add'} API Key
            </button>
            <button
              type="button"
              onClick={() => setShowBrightDataModal(false)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Token Add/Edit Modal */}
      <Modal
        isOpen={showTokenModal}
        onClose={() => {
          setShowTokenModal(false);
          setEditingToken(null);
          setTokenForm({ email: '', token: '', name: '' });
        }}
        title={editingToken ? 'Edit APIFY Token' : 'Add APIFY Token'}
        size="md"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              APIFY Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={tokenForm.email}
              onChange={(e) => handleTokenFormChange('email', e.target.value)}
              placeholder="email@example.com"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              The email address associated with your APIFY account
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              APIFY Token <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={tokenForm.token}
              onChange={(e) => handleTokenFormChange('token', e.target.value)}
              placeholder={editingToken ? "Leave empty to keep existing token" : "apify_api_xxxxxxxxxxxxxxxxxxxxx"}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
              required={!editingToken}
            />
            <p className="text-xs text-gray-500 mt-1">
              Your APIFY API token from {' '}
              <a
                href="https://console.apify.com/account/integrations"
                target="_blank"
                rel="noopener noreferrer"
                className="text-yellow-600 hover:text-yellow-700"
              >
                APIFY Console → Account → Integrations
              </a>
              {editingToken && " (leave empty to keep current token)"}
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Token Name (Optional)
            </label>
            <input
              type="text"
              value={tokenForm.name}
              onChange={(e) => handleTokenFormChange('name', e.target.value)}
              placeholder="Production Token, Development Token, etc."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              A friendly name to help you identify this token
            </p>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowTokenModal(false);
                setEditingToken(null);
                setTokenForm({ email: '', token: '', name: '' });
              }}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveApifyToken}
              disabled={!tokenForm.email || (!tokenForm.token && !editingToken)}
              className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {editingToken ? 'Update Token' : 'Add Token'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Actor Add/Edit Modal */}
      <Modal
        isOpen={showActorModal}
        onClose={() => {
          setShowActorModal(false);
          resetActorForm();
        }}
        title={editingActor ? 'Edit APIFY Actor' : 'Add APIFY Actor'}
        size="lg"
      >
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Actor ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={actorForm.id}
                onChange={(e) => handleActorFormChange('id', e.target.value)}
                placeholder="username/actor-name"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
                disabled={!!editingActor}
              />
              <p className="text-xs text-gray-500 mt-1">
                Format: username/actor-name (e.g., supreme_coder/apollo-scraper)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={actorForm.name}
                onChange={(e) => handleActorFormChange('name', e.target.value)}
                placeholder="Supreme Coder Apollo Scraper"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Daily Limit <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={actorForm.daily_limit}
                onChange={(e) => handleActorFormChange('daily_limit', parseInt(e.target.value) || 100)}
                min="1"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Maximum runs per day for this actor
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cost per 1000 Contacts <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                value={actorForm.cost_per_1000_contacts}
                onChange={(e) => handleActorFormChange('cost_per_1000_contacts', parseFloat(e.target.value) || 0.0)}
                min="0"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                USD cost per 1000 contacts for this actor
              </p>
            </div>
          </div>

          <div>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={actorForm.enabled}
                onChange={(e) => handleActorFormChange('enabled', e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Actor Enabled</span>
            </label>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Daily Limit Message
            </label>
            <textarea
              value={actorForm.limit_message}
              onChange={(e) => handleActorFormChange('limit_message', e.target.value)}
              placeholder="Custom message shown when daily limit is reached"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              rows="2"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Parameters (JSON)
                <span className="text-blue-600 ml-1 cursor-help" title="Default parameters that will be sent to this actor. These can be overridden by job-specific data.">ℹ️</span>
              </label>
              <textarea
                value={actorForm.default_params}
                onChange={(e) => handleActorFormChange('default_params', e.target.value)}
                placeholder={JSON.stringify({
                  "clean": true,
                  "maxItems": 500
                }, null, 2)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                rows="6"
              />
              <div className="text-xs text-gray-500 mt-1 space-y-1">
                <p>Default parameters for this actor (JSON format)</p>
                <details className="cursor-pointer">
                  <summary className="text-blue-600 hover:text-blue-700">📋 Real Examples</summary>
                  <div className="mt-1 p-2 bg-gray-50 rounded text-xs font-mono space-y-2">
                    <div>
                      <p><strong>Supreme Coder:</strong></p>
                      <p>{`{"count": 500, "excludeGuessedEmails": true, "getEmails": true, "startPage": 1}`}</p>
                    </div>
                    <div>
                      <p><strong>ApiDojo:</strong></p>
                      <p>{`{"clean": true, "maxItems": 500}`}</p>
                    </div>
                    <div>
                      <p><strong>Code Crafter:</strong></p>
                      <p>{`{"cleanOutput": true, "fileName": "Export", "totalRecords": 1000}`}</p>
                    </div>
                    <div>
                      <p><strong>Microworlds:</strong></p>
                      <p>{`{"contact_email_exclude_catch_all": true, "include_email": true, "max_result": 500}`}</p>
                    </div>
                  </div>
                </details>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Input Mapping (JSON)
                <span className="text-blue-600 ml-1 cursor-help" title="Maps frontend form fields to actor input parameters. Key = actor parameter, Value = job data field.">ℹ️</span>
              </label>
              <textarea
                value={actorForm.input_mapping}
                onChange={(e) => handleActorFormChange('input_mapping', e.target.value)}
                placeholder={JSON.stringify({
                  "url": "apollo_url",
                  "maxItems": "number_of_contacts"
                }, null, 2)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                rows="6"
              />
              <div className="text-xs text-gray-500 mt-1 space-y-1">
                <p>Maps job fields to actor input fields (JSON format)</p>
                <details className="cursor-pointer">
                  <summary className="text-blue-600 hover:text-blue-700">📋 Real Examples & Available Fields</summary>
                  <div className="mt-1 p-2 bg-gray-50 rounded text-xs font-mono space-y-2">
                    <div>
                      <p><strong>Available Job Fields:</strong></p>
                      <p>• apollo_url • number_of_contacts • list_name</p>
                    </div>
                    <div>
                      <p><strong>Supreme Coder mapping:</strong></p>
                      <p>{`{"searchUrl": "apollo_url", "count": "number_of_contacts"}`}</p>
                    </div>
                    <div>
                      <p><strong>Code Crafter mapping:</strong></p>
                      <p>{`{"url": "apollo_url", "totalRecords": "number_of_contacts", "fileName": "list_name"}`}</p>
                    </div>
                    <div>
                      <p><strong>Microworlds mapping:</strong></p>
                      <p>{`{"url": "apollo_url", "max_result": "number_of_contacts"}`}</p>
                    </div>
                    <div>
                      <p><strong>ApiDojo mapping:</strong></p>
                      <p>{`{"url": "apollo_url", "maxItems": "number_of_contacts"}`}</p>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                setShowActorModal(false);
                resetActorForm();
              }}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={saveApifyActor}
              disabled={!actorForm.id || !actorForm.name}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              {editingActor ? 'Update Actor' : 'Add Actor'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ScraperSettingsTab;